import { PackageTableData } from "../../../models/tables";
import { CommonFilterData, OptionItem } from "../../../models/filters";
import { Table, getFindingValueForSorting } from "../../utils";

export class PackageTable extends Table {
  // Store grouped packages to prevent duplication across pagination
  groupedByName: Record<string, PackageTableData[]> = {};
  allGroupedPackages: PackageTableData[] = [];

  constructor(data: PackageTableData[]) {
    super(data);
    this.groupPackagesByName();
  }

  // Group packages by name globally before pagination
  groupPackagesByName() {
    if (!this.tableData || !Array.isArray(this.tableData)) {
      this.groupedByName = {};
      this.allGroupedPackages = [];
      return;
    }

    // Clear existing grouped data
    this.groupedByName = {};

    // Group packages by name
    this.tableData.forEach((pkg) => {
      const name = pkg.name;
      if (!this.groupedByName[name]) {
        this.groupedByName[name] = [];
      }
      this.groupedByName[name].push(pkg);
    });

    // Convert to flat array with each package appearing only once
    this.allGroupedPackages = Object.keys(this.groupedByName).map(name => {
      // For simplicity, use the first package for each name
      // The template will still show all locations for that package
      return this.groupedByName[name][0];
    });
  }

  // Helper method to get the multiple versions text for a package group
  getVersionsText(packageName: string): string {
    if (!this.groupedByName[packageName] || this.groupedByName[packageName].length <= 1) {
      return "";
    }

    const uniqueVersions = new Set<string>();

    this.groupedByName[packageName].forEach(pkg => {
      // Use type assertion to access possible undefined properties
      const versionText = (pkg as any).version || (pkg as any).specifier || 'unspecified';
      uniqueVersions.add(versionText);
    });

    if (uniqueVersions.size === 1) {
      return Array.from(uniqueVersions)[0];
    }

    return `${uniqueVersions.size} versions`;
  }

  // Override pagedTableData to use pre-grouped packages
  pagedTableData() {
    // Re-group packages if needed
    if (this.allGroupedPackages.length === 0 && this.tableData.length > 0) {
      this.groupPackagesByName();
    }

    return this.allGroupedPackages.filter((_row: any, index: number) => {
      let start = (this.curPage - 1) * this.pageSize;
      let end = this.curPage * this.pageSize;
      if (index >= start && index < end) return true;
    });
  }

  sort(col: string) {
    if (this.sortCol === col) this.sortAsc = !this.sortAsc;
    else this.sortAsc = false;
    this.sortCol = col;
    this.tableData.sort((a: PackageTableData, b: PackageTableData) => {
      if (col === "risk") {
        if (!a.risk) return 1;
        if (!b.risk) return -1;
        if (a.risk.score < b.risk.score) return this.sortAsc ? 1 : -1;
        if (a.risk.score > b.risk.score) return this.sortAsc ? -1 : 1;
      }
      if (col === "findings") {
        if (getFindingValueForSorting(a.findings) < getFindingValueForSorting(b.findings))
          return this.sortAsc ? 1 : -1;
        if (getFindingValueForSorting(a.findings) > getFindingValueForSorting(b.findings))
          return this.sortAsc ? -1 : 1;
      }
      if (
        a[this.sortCol as keyof PackageTableData]! <
        b[this.sortCol as keyof PackageTableData]!
      )
        return this.sortAsc ? 1 : -1;
      if (
        a[this.sortCol as keyof PackageTableData]! >
        b[this.sortCol as keyof PackageTableData]!
      )
        return this.sortAsc ? -1 : 1;

      return 0;
    });

    // Re-group after sorting
    this.groupPackagesByName();
  }

  filterTable(filterData: CommonFilterData[]) {
    let tableDataCopy: PackageTableData[] = [...this.constantTableData];

    tableDataCopy = tableDataCopy.filter((item: PackageTableData) => !item.archived_at);

    filterData.forEach((filterItem: CommonFilterData) => {
      const validOptions: OptionItem[] = filterItem.options.filter((optionItem: OptionItem) => optionItem.checked);

      if (validOptions.length === 0) return;

      tableDataCopy = tableDataCopy.filter((item: PackageTableData) => !!validOptions.find((option: OptionItem) => {
        if (filterItem.col === 'ecosystems') {
          return item.ecosystems.includes(option.value);
        }
        if (filterItem.col === 'risk') {
          return item.risk.label.toLowerCase() === option.value.toLowerCase();
        }
      }));
    });

    this.tableData = tableDataCopy;

    // Re-group after filtering
    this.groupPackagesByName();

    // Reset current page number if filtered table data length is smaller than page size
    if (this.allGroupedPackages.length < this.pageSize) {
      this.curPage = 1;
    }
  }

  searchTable(event: Event) {
    const inputElement = event.target as HTMLInputElement;
    const searchText = inputElement.value;

    if (searchText !== "") {
      this.tableData = this.constantTableData
        .filter((item: PackageTableData) => !item.archived_at)
        .filter((item: PackageTableData) =>
          item.name.toLowerCase().includes(searchText.toLowerCase())
        );
    } else {
      this.tableData = this.constantTableData.filter((item: PackageTableData) => !item.archived_at);
    }

    // Re-group after searching
    this.groupPackagesByName();
  }

  // Override loadData to re-group packages after loading data
  async loadData(repo: any, repoFn: string, repoFnArgs?: Array<any>, filterFn?: string) {
    const result = await super.loadData(repo, repoFn, repoFnArgs, filterFn);
    this.groupPackagesByName();
    return result;
  }

  trimPaths(path: string, paths: string[]): string {
    if (!path) return '';

    for (const prefix of paths) {
      if (path.startsWith(prefix)) {
        // Remove the matching prefix and any leading slash
        return path.slice(prefix.length).replace(/^\//, '');
      }
    }

    return path;
  }
}
